import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, num } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, UasRegistration } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const ANY_OFFICER = ['UAS_OFFICER', 'AIRSPACE_OFFICER', 'FLIGHT_OPS_INSPECTOR'];
const UAS_OFFICER_ROLES = ['UAS_OFFICER'];
const STATES = ['APPLICATION', 'FEE_PENDING', 'REGISTERED', 'SUSPENDED', 'REVOKED', 'EXPIRED', 'REJECTED', 'WITHDRAWN'];

/** M23 — drone/UAS registry: the 15-min registration flow, suspend/reinstate/revoke/withdraw. */
export function UasRegistrations() {
  const { persona } = useAuth();
  const canView = persona ? persona.roles.some((r) => ANY_OFFICER.includes(r)) : false;
  const isUasOfficer = persona ? persona.roles.some((r) => UAS_OFFICER_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showRegister, setShowRegister] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m23/uas-registrations?${params.toString()}`;
  }, [status, appliedQ]);

  const listQ = useApiQuery<Page<UasRegistration>>(path);

  return (
    <div>
      <PageHeader
        title="UAS / Drone Registrations"
        subtitle="Drone registry — 15-minute registration flow, mark issued to DocVault (M23 — SCAA)."
        actions={
          <>
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                setAppliedQ(q);
              }}
            >
              <input className="field-input !w-44" placeholder="Mark, make, model…" value={q} onChange={(e) => setQ(e.target.value)} />
              <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All statuses</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>
                    {s.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
              <button type="submit" className="btn-secondary">
                Search
              </button>
            </form>
            {isUasOfficer ? (
              <button className="btn-primary" onClick={() => setShowRegister(true)}>
                + Register drone
              </button>
            ) : null}
          </>
        }
      />

      {!canView ? <RoleNotice roles={ANY_OFFICER} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No UAS registrations match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Registration mark</th>
                <th className="table-head">Make / model</th>
                <th className="table-head">Class</th>
                <th className="table-head">Operator</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((r) => (
                <tr key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(r.id)}>
                  <td className="table-cell font-mono text-xs font-semibold">{r.registrationMark || '(pending)'}</td>
                  <td className="table-cell">
                    {r.droneMake} {r.droneModel}
                  </td>
                  <td className="table-cell text-xs text-slate-500">{r.uasClass}</td>
                  <td className="table-cell text-xs text-slate-500">
                    {r.operatorType} · {r.operatorId ?? '—'}
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={r.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showRegister ? (
        <RegisterUasModal
          onClose={() => setShowRegister(false)}
          onCreated={() => {
            setShowRegister(false);
            listQ.reload();
          }}
        />
      ) : null}

      {selectedId ? (
        <UasDetailModal
          id={selectedId}
          canAct={isUasOfficer}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
    </div>
  );
}

function RegisterUasModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [operatorType, setOperatorType] = useState<'PERSON' | 'ENTITY'>('PERSON');
  const [operatorId, setOperatorId] = useState('');
  const [droneMake, setDroneMake] = useState('');
  const [droneModel, setDroneModel] = useState('');
  const [droneSerial, setDroneSerial] = useState('');
  const [mtowKg, setMtowKg] = useState('');
  const [insurancePolicyRef, setInsurancePolicyRef] = useState('');
  const [insuranceExpiresAt, setInsuranceExpiresAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m23/uas-registrations', {
        operatorType,
        operatorId: operatorId.trim(),
        droneMake: droneMake.trim(),
        droneModel: droneModel.trim(),
        droneSerial: droneSerial.trim(),
        mtowKg: Number.parseFloat(mtowKg),
        insurancePolicyRef: insurancePolicyRef.trim(),
        insuranceExpiresAt: new Date(insuranceExpiresAt).toISOString(),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register drone');
    } finally {
      setBusy(false);
    }
  };

  const valid = operatorId.trim() && droneMake.trim() && droneModel.trim() && droneSerial.trim() && mtowKg && insurancePolicyRef.trim() && insuranceExpiresAt;

  return (
    <Modal title="Register a drone" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Operator type">
          <select className="field-input" value={operatorType} onChange={(e) => setOperatorType(e.target.value as 'PERSON' | 'ENTITY')}>
            <option value="PERSON">Person (NIN)</option>
            <option value="ENTITY">Entity (business id)</option>
          </select>
        </Field>
        <Field label={operatorType === 'PERSON' ? 'Operator NIN' : 'Operator business id'} className="col-span-2">
          <input className="field-input" value={operatorId} onChange={(e) => setOperatorId(e.target.value)} />
        </Field>
        <Field label="Make">
          <input className="field-input" value={droneMake} onChange={(e) => setDroneMake(e.target.value)} placeholder="DJI" />
        </Field>
        <Field label="Model">
          <input className="field-input" value={droneModel} onChange={(e) => setDroneModel(e.target.value)} placeholder="Mavic 3" />
        </Field>
        <Field label="Serial no.">
          <input className="field-input" value={droneSerial} onChange={(e) => setDroneSerial(e.target.value)} />
        </Field>
        <Field label="MTOW (kg)">
          <input className="field-input" type="number" step="0.001" value={mtowKg} onChange={(e) => setMtowKg(e.target.value)} />
        </Field>
        <Field label="Insurance policy ref">
          <input className="field-input" value={insurancePolicyRef} onChange={(e) => setInsurancePolicyRef(e.target.value)} />
        </Field>
        <Field label="Insurance expires">
          <input className="field-input" type="date" value={insuranceExpiresAt} onChange={(e) => setInsuranceExpiresAt(e.target.value)} />
        </Field>
      </div>
      <p className="mt-2 text-xs text-slate-500">UAS class (MICRO/SMALL/MEDIUM/LARGE) is derived server-side from MTOW — never client-supplied.</p>
      <button className="btn-primary mt-4" disabled={busy || !valid} onClick={submit}>
        {busy ? 'Registering…' : 'Submit registration'}
      </button>
    </Modal>
  );
}

function UasDetailModal({ id, canAct, onClose, onChanged }: { id: string; canAct: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<UasRegistration>(`/api/v1/m23/uas-registrations/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [showAction, setShowAction] = useState<'suspend' | 'revoke' | 'withdraw' | null>(null);

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const r = q.data;

  return (
    <Modal title={r ? `${r.registrationMark || 'Pending'} — ${r.droneMake} ${r.droneModel}` : 'UAS registration'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {r ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={r.status} />} />
            <Detail label="Class" value={r.uasClass} />
            <Detail label="MTOW" value={`${num(r.mtowKg, { maximumFractionDigits: 3 })} kg`} />
            <Detail label="Serial no." value={r.droneSerial} mono />
            <Detail label="Insurance ref" value={r.insurancePolicyRef} />
            <Detail label="Insurance expires" value={formatDate(r.insuranceExpiresAt)} />
            {r.validTo ? <Detail label="Valid to" value={formatDate(r.validTo)} /> : null}
          </div>
          {r.rejectionReason ? <ErrorNote message={`Rejected: ${r.rejectionReason}`} /> : null}
          {r.suspendedReason ? <ErrorNote message={`Suspended: ${r.suspendedReason}`} /> : null}
          {r.revokedReason ? <ErrorNote message={`Revoked: ${r.revokedReason}`} /> : null}

          {r.operations && r.operations.length > 0 ? (
            <div>
              <div className="field-label">Operation applications</div>
              <div className="space-y-1.5">
                {r.operations.map((o) => (
                  <div key={o.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-1.5 text-sm">
                    <span>
                      {o.appRef} · {o.operationType.replaceAll('_', ' ')}
                    </span>
                    <StatusBadge status={o.state} />
                  </div>
                ))}
              </div>
              <p className="mt-1 text-xs text-slate-400">Manage operation applications from the UAS Operations workspace.</p>
            </div>
          ) : null}

          {canAct ? (
            <div className="flex flex-wrap gap-2">
              {r.status === 'REGISTERED' ? (
                showAction === 'suspend' ? (
                  <div className="flex items-end gap-2">
                    <Field label="Reason">
                      <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    <button className="btn-danger" disabled={busy || reason.trim().length < 3} onClick={() => call(() => api.post(`/api/v1/m23/uas-registrations/${r.id}/suspend`, { reason: reason.trim() }))}>
                      Confirm suspend
                    </button>
                  </div>
                ) : (
                  <button className="btn-danger" onClick={() => setShowAction('suspend')}>
                    Suspend
                  </button>
                )
              ) : null}
              {r.status === 'SUSPENDED' ? (
                <button className="btn-success" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m23/uas-registrations/${r.id}/reinstate`))}>
                  Reinstate
                </button>
              ) : null}
              {r.status === 'REGISTERED' || r.status === 'SUSPENDED' ? (
                showAction === 'revoke' ? (
                  <div className="flex items-end gap-2">
                    <Field label="Reason">
                      <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    <button className="btn-danger" disabled={busy || reason.trim().length < 3} onClick={() => call(() => api.post(`/api/v1/m23/uas-registrations/${r.id}/revoke`, { reason: reason.trim() }))}>
                      Confirm revoke
                    </button>
                  </div>
                ) : (
                  <button className="btn-danger" onClick={() => setShowAction('revoke')}>
                    Revoke
                  </button>
                )
              ) : null}
              {!['REVOKED', 'REJECTED', 'WITHDRAWN', 'EXPIRED'].includes(r.status) ? (
                showAction === 'withdraw' ? (
                  <div className="flex items-end gap-2">
                    <Field label="Reason (optional)">
                      <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    <button className="btn-secondary" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m23/uas-registrations/${r.id}/withdraw`, reason.trim() ? { reason: reason.trim() } : {}))}>
                      Confirm withdraw
                    </button>
                  </div>
                ) : (
                  <button className="btn-secondary" onClick={() => setShowAction('withdraw')}>
                    Withdraw
                  </button>
                )
              ) : null}
            </div>
          ) : (
            <RoleNotice roles={UAS_OFFICER_ROLES} />
          )}
        </div>
      ) : null}
    </Modal>
  );
}
